import { uniq } from "@-/com.npmjs.array-utils-ts";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { StaticDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.static";
import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import { getHskLevelTotals } from "@-/dev.improve.haniverse.data.hsk-level-totals";
import { getUserCardStage } from "@-/dev.improve.haniverse.lib.cards.user-card.mastery";
import type { WordCardKind } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import {
  tableNameMatcher,
  useTableChangeVersion,
} from "@-/dev.improve.haniverse.lib.database.change";
import type {
  CardsByType,
  CardTypeKey,
  LearnedGraphemeRow,
  LearnedStudyCardRow,
  LearnedWordRow,
  ProgressStageKey,
} from "@-/dev.improve.haniverse.lib.learning-stats.entities";
import {
  getHskWordCountsByLevel,
  getHskWordLevelsFromDb,
} from "@-/dev.improve.haniverse.lib.morpheme.grade.hsk";
import {
  cardContentDrillType,
  isCardContentKindCountedInStats,
  resolveUserCardContentKind,
} from "@-/dev.improve.haniverse.lib.cards.card-content";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import { useActorUserDataQueryOptions } from "@-/dev.improve.haniverse.lib.user";
import { useLocalDayKey } from "@-/dev.improve.haniverse.lib.time.local-day";
import { computeLearningStatsCatalog, computeLearningStatsCore } from "#compute-learning-stats";
import { buildHskLevelProgress } from "#learning-stats-series";
import {
  readStreamingCoverageHistory,
  readStreamingReviewHistory,
} from "#read-streaming-review-history";
import { readRecentStageEntries } from "#read-recent-stage-entries";
import { readStageCards } from "#read-stage-cards";
import { runLearningStatsQueryStep } from "#run-learning-stats-query-step";
import { selectLearnedMeaningStudyCardsByGrapheme } from "#select-learned-meaning-study-cards-by-grapheme";
import { selectLearnedWordStudyCardsByWord } from "#select-learned-word-study-cards-by-word";
import { hasLength } from "@-/com.npmjs.ts-array-length";
import { usePrefetchedSuspenseQuery, useQuery } from "@-/com.npmjs.tanstack__react-query";

/** A zeroed tally for every card type, so a stage with no cards of a type still reads `0`. */
function emptyCardsByType(): CardsByType {
  return {
    meaning: 0,
    pronunciation: 0,
    "sentence-glyphs": 0,
    "sentence-speech": 0,
    "word-sentence": 0,
  };
}

/**
 * Which drill a study card is, for the per-type breakdown.
 *
 * Goes through the card-content registry exactly as the deck's tile type does, rather than
 * inferring from overlay PRESENCE: all three word drills share one overlay table, so presence
 * alone would collapse them into one bucket. Routing both surfaces through
 * `cardContentDrillType` is what keeps the Overall tab's rows and the deck's tiles agreeing on
 * what a card is — and therefore on which icon it gets.
 *
 * Returns `undefined` for a card whose overlay has not synced yet (or whose kind this build does
 * not know); such a card is still counted in its stage total, just not attributed to a type.
 */
function studyCardType(row: {
  userGraphemeMeaningCard?: unknown;
  userGraphemePronunciationCard?: unknown;
  userWordSentenceCard?: { kind: WordCardKind } | null;
}): CardTypeKey | undefined {
  const contentKind = resolveUserCardContentKind(row);
  // A quiz is not a drill: counted in its stage total (when quizzes count at all), not by type.
  if (contentKind === null || contentKind === "quiz") {
    return undefined;
  }
  // Only `kind` decides the type; the payload fields are irrelevant here, so they are stubbed
  // rather than re-read.
  return cardContentDrillType(
    contentKind === "grapheme-meaning" || contentKind === "grapheme-pronunciation"
      ? { grapheme: "", kind: contentKind, pinyin: "" }
      : { kind: contentKind, pinyin: "", word: "" },
  ).kind;
}

// Stats consumes a temporary projection and caches only its aggregates. The generic
// actor-query cache strips projections and would retain every card's full overlays/FSRS row.
const LEARNING_STATS_USER_CARDS_CONFIG = {
  columns: { deletedAt: true, id: true, manuallyMasteredAt: true, startedStudyingAt: true },
  where: { fsrsCardId: { isNotNull: true } },
  with: {
    fsrsCard: {
      columns: { due: true, lastReview: true, reps: true, scheduledDays: true, state: true },
    },
    userGraphemeMeaningCard: { columns: { grapheme: true } },
    userGraphemePronunciationCard: { columns: { grapheme: true } },
    // Joined to tell quiz cards apart: they count only with "Show quiz cards in stats" on.
    userQuizCard: { columns: { key: true } },
    // Joined for the per-card-type breakdown. An overlay that is not joined makes its cards
    // resolve to no content and fall into the `unknown` bucket, so the word cards must come
    // along — and with their `kind`, which is the ONLY thing separating the three word drills
    // (they share this one table). `word` keys the learned-word tally.
    userWordSentenceCard: { columns: { kind: true, word: true } },
  },
} as const;

const LEARNING_STATS_CORE_TABLES = [
  "user",
  "user_card",
  "fsrs_card",
  "user_grapheme_meaning_card",
  "user_grapheme_pronunciation_card",
  // The per-card-type breakdown reads this overlay, so a word-card write has to invalidate the
  // stats query the same way a grapheme-card write does.
  "user_word_sentence_card",
  // Quiz cards are left out of the totals (or counted) by their overlay.
  "user_quiz_card",
  "collection",
] as const;

const LEARNING_STATS_REVIEW_HISTORY_TABLES = ["fsrs_card_review_log", "user_card_review"] as const;

/**
 * The page's suspense/prefetch slice. ACTOR DB ONLY: the static catalog (HSK levels, corpus
 * frequency) is deliberately not part of this key or read — on native the static DB is served
 * online, and holding the whole page behind that round-trip is what made the Stats tab feel
 * slow. The catalog joins in via the separate, non-blocking `useLearningStatsCatalogQueryOptions`.
 */
export function useGetLearningStatsQueryKey() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (context) => context.db);
  const actorUserDataQueryOptions = useActorUserDataQueryOptions();
  const withTableChangeVersion = useTableChangeVersion(
    tableNameMatcher(LEARNING_STATS_CORE_TABLES),
  );

  return () =>
    withTableChangeVersion(["learningStats", { actorDb, actorUserDataQueryOptions }] as const);
}

export function useGetLearningStatsReviewHistoryQueryKey() {
  // Daily totals, streaks and rolling windows change at midnight without a database write.
  const dayKey = useLocalDayKey();
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (context) => context.db);
  const coreQueryOptions = useLearningStatsQueryOptions();
  const withTableChangeVersion = useTableChangeVersion(
    tableNameMatcher(LEARNING_STATS_REVIEW_HISTORY_TABLES),
  );

  return () =>
    withTableChangeVersion([
      "learningStatsReviewHistory",
      { actorDb, coreQueryOptions, dayKey },
    ] as const);
}

const [, guardedLearningStatsQueryFn] = createUseQueryOptions(
  useGetLearningStatsQueryKey,
  async ({ client, queryKey: [_, { actorDb, actorUserDataQueryOptions }] }) => {
    const [{ user }, studyCardRows] = await Promise.all([
      runLearningStatsQueryStep("actor user", () => client.fetchQuery(actorUserDataQueryOptions)),
      runLearningStatsQueryStep("study cards", () =>
        actorDb(import.meta.location).query.userCard.findMany(LEARNING_STATS_USER_CARDS_CONFIG),
      ),
    ]);

    const activeStudyCards = studyCardRows.filter(
      (row) =>
        row.deletedAt === null &&
        isCardContentKindCountedInStats(resolveUserCardContentKind(row), user),
    );

    const cardsByStage: Record<ProgressStageKey, number> = {
      familiar: 0,
      mastered: 0,
      new: 0,
      proficient: 0,
    };
    const cardsByStageAndType: Record<ProgressStageKey, CardsByType> = {
      familiar: emptyCardsByType(),
      mastered: emptyCardsByType(),
      new: emptyCardsByType(),
      proficient: emptyCardsByType(),
    };
    const graphemesByStage: Record<ProgressStageKey, string[]> = {
      familiar: [],
      mastered: [],
      new: [],
      proficient: [],
    };
    for (const row of activeStudyCards) {
      // `UserCardStage` and `ProgressStageKey` share the same four names, so the
      // stage doubles as the tally key directly.
      const stage = getUserCardStage({
        fsrsCard: row.fsrsCard ?? null,
        manuallyMasteredAt: row.manuallyMasteredAt,
      });
      cardsByStage[stage] += 1;
      const cardType = studyCardType(row);
      if (cardType !== undefined) {
        cardsByStageAndType[stage][cardType] += 1;
      }
      const grapheme =
        row.userGraphemeMeaningCard?.grapheme ?? row.userGraphemePronunciationCard?.grapheme;
      if (grapheme !== undefined) {
        graphemesByStage[stage].push(grapheme);
      }
    }

    const uniqueGraphemesByStage: Record<ProgressStageKey, readonly string[]> = {
      familiar: uniq(graphemesByStage.familiar),
      mastered: uniq(graphemesByStage.mastered),
      new: uniq(graphemesByStage.new),
      proficient: uniq(graphemesByStage.proficient),
    };

    const learnedStudyCards: LearnedStudyCardRow[] = [];
    for (const [glyph, studyCard] of selectLearnedMeaningStudyCardsByGrapheme(activeStudyCards)) {
      learnedStudyCards.push({
        glyph,
        id: studyCard.id,
        lastReview: studyCard.fsrsCard?.lastReview ?? null,
        manuallyMasteredAt: studyCard.manuallyMasteredAt,
        reps: studyCard.fsrsCard?.reps ?? 0,
        scheduledDays: studyCard.fsrsCard?.scheduledDays ?? 0,
        stage: getUserCardStage({
          fsrsCard: studyCard.fsrsCard ?? null,
          manuallyMasteredAt: studyCard.manuallyMasteredAt,
        }),
        startedStudyingAt: studyCard.startedStudyingAt ?? new Date(0).toISOString(),
        state: studyCard.fsrsCard?.state ?? 0,
      });
    }

    const learnedWords: LearnedWordRow[] = [];
    for (const [word, studyCard] of selectLearnedWordStudyCardsByWord(activeStudyCards)) {
      learnedWords.push({
        stage: getUserCardStage({
          fsrsCard: studyCard.fsrsCard ?? null,
          manuallyMasteredAt: studyCard.manuallyMasteredAt,
        }),
        word,
      });
    }

    const nowMs = Date.now();
    let dueTodayCount = 0;
    for (const row of activeStudyCards) {
      const dueIso = row.fsrsCard?.due;
      if (dueIso === undefined || dueIso === null) {
        continue;
      }
      if (new Date(dueIso).getTime() <= nowMs) {
        dueTodayCount += 1;
      }
    }

    return computeLearningStatsCore({
      cardsByStage,
      cardsByStageAndType,
      dueTodayCount,
      graphemesByStage: uniqueGraphemesByStage,
      hskVersion: user.hskVersion,
      learnedStudyCards,
      learnedWords,
      studyCardStateList: activeStudyCards.map((studyCard) => studyCard.fsrsCard?.state ?? 0),
    });
  },
);

/**
 * Bound the aggregate guard as well as the named reads inside it. The shared query
 * factory applies its write-clock read guard before entering the query function,
 * so an outer wrapper is required to prevent that gate from suspending forever.
 */
export const learningStatsQueryFn: typeof guardedLearningStatsQueryFn = (context) =>
  runLearningStatsQueryStep("core aggregate read gate", () => guardedLearningStatsQueryFn(context));

export function useLearningStatsQueryOptions() {
  const getQueryKey = useGetLearningStatsQueryKey();
  return {
    queryFn: learningStatsQueryFn,
    get queryKey() {
      return getQueryKey();
    },
  };
}

/**
 * The static-catalog slice, keyed on the core query's options: the core key carries the actor
 * change version, so a study-card write re-derives the catalog through the same invalidation
 * (the core read itself is served from the query cache).
 */
export function useGetLearningStatsCatalogQueryKey() {
  const [staticDb] = useContextSelectors(StaticDatabaseContext, (c) => c.db);
  const coreQueryOptions = useLearningStatsQueryOptions();

  return () => ["learningStatsCatalog", { coreQueryOptions, staticDb }] as const;
}

const [, guardedLearningStatsCatalogQueryFn] = createUseQueryOptions(
  useGetLearningStatsCatalogQueryKey,
  async ({ client, queryKey: [_, { coreQueryOptions, staticDb }] }) => {
    const core = await runLearningStatsQueryStep("core stats", () =>
      client.fetchQuery(coreQueryOptions),
    );
    const learnedFrontList = core.learnedStudyCards.map((studyCard) => studyCard.glyph);

    const graphemeRows = hasLength(learnedFrontList, 0)
      ? []
      : await runLearningStatsQueryStep("static graphemes", () =>
          staticDb(import.meta.location).query.grapheme.findMany({
            columns: { glyph: true },
            where: { glyph: { in: learnedFrontList } },
            with: {
              prcData: {
                columns: {
                  minHsk2010Level: true,
                  minHsk2021Level: true,
                  minHsk2026Level: true,
                },
              },
              subtlex: { columns: { frequencyPerMillion: true } },
            },
          }),
        );

    const graphemeByGlyph = new Map<string, (typeof graphemeRows)[number]>();
    for (const row of graphemeRows) {
      graphemeByGlyph.set(row.glyph, row);
    }

    const learnedGraphemes: LearnedGraphemeRow[] = [];
    for (const studyCard of core.learnedStudyCards) {
      const grapheme = graphemeByGlyph.get(studyCard.glyph);
      learnedGraphemes.push({
        ...studyCard,
        frequencyPerMillion: grapheme?.subtlex?.frequencyPerMillion ?? null,
        hsk2010Level: grapheme?.prcData?.minHsk2010Level ?? null,
        hsk2021Level: grapheme?.prcData?.minHsk2021Level ?? null,
        hsk2026Level: grapheme?.prcData?.minHsk2026Level ?? null,
      });
    }

    // The HSK denominator (graphemes per level) is static, so it's precomputed at
    // build time — no on-device scan of the grapheme table. See `lib/learning-stats`.
    const hskTotalsByLevel = new Map(getHskLevelTotals()[core.hskVersion]);

    return computeLearningStatsCatalog({
      hskTotalsByLevel,
      hskVersion: core.hskVersion,
      learnedGraphemes,
    });
  },
);

export const learningStatsCatalogQueryFn: typeof guardedLearningStatsCatalogQueryFn = (context) =>
  runLearningStatsQueryStep("catalog aggregate read gate", () =>
    guardedLearningStatsCatalogQueryFn(context),
  );

export function useLearningStatsCatalogQueryOptions() {
  const getQueryKey = useGetLearningStatsCatalogQueryKey();
  return {
    queryFn: learningStatsCatalogQueryFn,
    get queryKey() {
      return getQueryKey();
    },
  };
}

/**
 * Learned words per HSK level, keyed like the catalog on the core query's options (a word-card
 * write re-derives it). A separate slice so the character catalog never waits on word levels.
 */
export function useGetLearningStatsWordHskProgressQueryKey() {
  const [staticDb] = useContextSelectors(StaticDatabaseContext, (c) => c.db);
  const coreQueryOptions = useLearningStatsQueryOptions();

  return () => ["learningStatsWordHskProgress", { coreQueryOptions, staticDb }] as const;
}

const [, guardedLearningStatsWordHskProgressQueryFn] = createUseQueryOptions(
  useGetLearningStatsWordHskProgressQueryKey,
  async ({ client, queryKey: [_, { coreQueryOptions, staticDb }] }) => {
    const core = await runLearningStatsQueryStep("core stats for word HSK levels", () =>
      client.fetchQuery(coreQueryOptions),
    );
    const db = staticDb(import.meta.location);
    const words = core.learnedWords.map((row) => row.word);
    const [levelByWord, totalsByLevel] = await Promise.all([
      hasLength(words, 0)
        ? new Map<string, number>()
        : runLearningStatsQueryStep("static word HSK levels", () =>
            getHskWordLevelsFromDb(db, core.hskVersion, words),
          ),
      runLearningStatsQueryStep("static word HSK totals", () =>
        getHskWordCountsByLevel(db, core.hskVersion),
      ),
    ]);
    return buildHskLevelProgress(
      core.learnedWords.map((row) => ({
        level: levelByWord.get(row.word) ?? null,
        stage: row.stage,
      })),
      totalsByLevel,
    );
  },
);

export const learningStatsWordHskProgressQueryFn: typeof guardedLearningStatsWordHskProgressQueryFn =
  (context) =>
    runLearningStatsQueryStep("word HSK aggregate read gate", () =>
      guardedLearningStatsWordHskProgressQueryFn(context),
    );

export function useLearningStatsWordHskProgressQueryOptions() {
  const getQueryKey = useGetLearningStatsWordHskProgressQueryKey();
  return {
    queryFn: learningStatsWordHskProgressQueryFn,
    get queryKey() {
      return getQueryKey();
    },
  };
}

const [, guardedLearningStatsReviewHistoryQueryFn] = createUseQueryOptions(
  useGetLearningStatsReviewHistoryQueryKey,
  async ({ client, queryKey: [_, { actorDb, coreQueryOptions }], signal }) => {
    const core = await runLearningStatsQueryStep("core stats for stage history", () =>
      client.fetchQuery(coreQueryOptions),
    );
    return readStreamingReviewHistory({
      actorDb,
      cards: core.learnedStudyCards,
      signal,
    });
  },
);

// Each bounded page has its own deadline. A whole-history timeout would reject
// healthy large accounts and leave their abandoned scan running in the background.
export const learningStatsReviewHistoryQueryFn = guardedLearningStatsReviewHistoryQueryFn;

export function useLearningStatsReviewHistoryQueryOptions() {
  const getQueryKey = useGetLearningStatsReviewHistoryQueryKey();
  return {
    queryFn: learningStatsReviewHistoryQueryFn,
    get queryKey() {
      return getQueryKey();
    },
  };
}

function useGetLearningStatsCoverageHistoryQueryKey() {
  // The timeline's final endpoint is today, even if no review occurred today.
  const dayKey = useLocalDayKey();
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (context) => context.db);
  const catalogQueryOptions = useLearningStatsCatalogQueryOptions();
  const withTableChangeVersion = useTableChangeVersion(
    tableNameMatcher(LEARNING_STATS_REVIEW_HISTORY_TABLES),
  );
  return () =>
    withTableChangeVersion([
      "learningStatsCoverageHistory",
      { actorDb, catalogQueryOptions, dayKey },
    ] as const);
}

const [useLearningStatsCoverageHistoryQueryOptions] = createUseQueryOptions(
  useGetLearningStatsCoverageHistoryQueryKey,
  async ({ client, queryKey: [_, { actorDb, catalogQueryOptions }], signal }) => {
    const catalog = await runLearningStatsQueryStep("catalog for coverage history", () =>
      client.fetchQuery(catalogQueryOptions),
    );
    return readStreamingCoverageHistory({ actorDb, cards: catalog.learnedGraphemes, signal });
  },
);

export { useLearningStatsCoverageHistoryQueryOptions };

function useGetLearningStatsRecentStageEntriesQueryKey() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (context) => context.db);
  // A card's stage lives on the core tables; the review that moved it there lives in the history.
  const withTableChangeVersion = useTableChangeVersion(
    tableNameMatcher([...LEARNING_STATS_CORE_TABLES, ...LEARNING_STATS_REVIEW_HISTORY_TABLES]),
  );
  return () => withTableChangeVersion(["learningStatsRecentStageEntries", { actorDb }] as const);
}

const [useLearningStatsRecentStageEntriesQueryOptions] = createUseQueryOptions(
  useGetLearningStatsRecentStageEntriesQueryKey,
  async ({ queryKey: [_, { actorDb }], signal }) => readRecentStageEntries({ actorDb, signal }),
);

export { useLearningStatsRecentStageEntriesQueryOptions };

function useGetLearningStatsStageCardsQueryKey() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (context) => context.db);
  const withTableChangeVersion = useTableChangeVersion(
    tableNameMatcher(LEARNING_STATS_CORE_TABLES),
  );
  return () => withTableChangeVersion(["learningStatsStageCards", { actorDb }] as const);
}

const [useLearningStatsStageCardsQueryOptions] = createUseQueryOptions(
  useGetLearningStatsStageCardsQueryKey,
  async ({ queryKey: [_, { actorDb }] }) => readStageCards(actorDb),
);

export { useLearningStatsStageCardsQueryOptions };

/** All card tiles for the selected stage, loaded independently of the aggregate counts. */
export function useLearningStatsStageCardsQuery() {
  return useQuery(useLearningStatsStageCardsQueryOptions());
}

/**
 * The core (actor-DB) stats for a leaf under `LearningStatsPageSuspense`. The Stats navigators
 * prefetch this slice and the page wrapper suspends on it for first paint, so a leaf reads it as
 * a cache hit and receives the data non-optionally.
 */
export function useLearningStatsSuspense() {
  return usePrefetchedSuspenseQuery(useLearningStatsQueryOptions());
}

/**
 * The static-catalog slice (HSK levels, SUBTLEX coverage). PROGRESSIVE: deliberately not
 * prefetched and never suspends, because on native the static DB is served online — each
 * consumer renders its own pending / error / empty states from the discriminated result.
 */
export function useLearningStatsCatalogQuery() {
  return useQuery(useLearningStatsCatalogQueryOptions());
}

/**
 * Learned words per HSK level. PROGRESSIVE, like the catalog: it reads the static catalog (served
 * online on native), so its consumer renders its own pending / error / empty states.
 */
export function useLearningStatsWordHskProgressQuery() {
  return useQuery(useLearningStatsWordHskProgressQueryOptions());
}

/**
 * The review-history slice. PROGRESSIVE: a large or stalled history read fails on its own
 * without holding the page behind the Suspense fallback.
 */
export function useLearningStatsReviewHistoryQuery() {
  return useQuery(useLearningStatsReviewHistoryQueryOptions());
}

/**
 * The weighted coverage history. PROGRESSIVE, and only enabled once the catalog it joins has
 * loaded: until then (and forever, if the catalog fails) it stays `pending` — so a consumer that
 * must tell "still loading" from "cannot load" reads `useLearningStatsCatalogQuery()` as well.
 */
export function useLearningStatsCoverageHistoryQuery() {
  const catalogQuery = useLearningStatsCatalogQuery();
  return useQuery({
    ...useLearningStatsCoverageHistoryQueryOptions(),
    enabled: catalogQuery.isSuccess,
  });
}

/**
 * Per progress stage, the cards that most recently entered it. PROGRESSIVE: it reads review
 * history (newest first, only as far back as needed), so the stage surfaces paint their counts
 * from the core slice and fill their card rows when this lands.
 */
export function useLearningStatsRecentStageEntriesQuery() {
  return useQuery(useLearningStatsRecentStageEntriesQueryOptions());
}
